import { Box, Typography, Button, IconButton, styled, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconContainerCircle } from "../../components-new/common";
import {
  BookmarkIcon,
  ContextIcon,
  PlusIcon,
  RightArrowIcon,
  LeftArrowIcon,
} from "../../components-new/icons";
import { useDispatch, useSelector } from "react-redux";
import { setEditItemIndex, setEditStep } from "../../Redux/TestAuthoring/TestAuthoring";
import { useEffect, useState } from "react";
import CustomMenu from "../../components-new/common/CustomMenu";
import { Package } from "lucide-react";

import { useAddBlankStep, useUpdateTestDataQuery } from "../../useQuery/index";
import { useNotification } from "../../hooks";
import { useQueryClient } from "@tanstack/react-query";

const StyledIconBtn = styled(IconButton)`
  border-radius: 0;
  padding: 10px;

  &:disabled {
    cursor: not-allowed;
    pointer-events: auto;
  }
`;

const StepText = styled("span")`
  padding-left: 30px;
  padding-right: 30px;
`;

const sharedActionButtonSx = {
  textTransform: "none",
  borderRadius: "6px",
  fontSize: "14px",
  fontWeight: 600,
  minHeight: "40px",
  px: 2.25,
  py: 0.9,
  lineHeight: 1.2,
  whiteSpace: "nowrap",
};
const EditHeader = ({
  editItem,
  handleAddStep,
  handleSaveStep,
  handleMerge,
  isReadOnly = false,
}) => {
  const dispatch = useDispatch();
  const notify = useNotification();
  const queryClient = useQueryClient();

  const testCasesList = useSelector((state) => state.authoringSlice.testCases);
  const stepDetails = useSelector((state) => state.authoringSlice.editStep);
  const currentEditItemIndex = useSelector((state) => state.authoringSlice.editItemIndex);
  const selectedModuleID = useSelector((state) => state.authoringSlice.selectedComponent);
  const { mutate: addBlankStep } = useAddBlankStep({
    onSuccess: () => {
      notify("Step Added Successfully", "success");
      queryClient.invalidateQueries(["latestRecordings"]);
    },
    onError: (error) => {
      notify(`Error Adding Step ${error}`);
    },
  });

  const [disableIncrement, setDisableIncrement] = useState(false);
  const [disableDecrement, setDisableDecrement] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const { mutate: updateTestData, isPending: isCreatingReferenceVariable } = useUpdateTestDataQuery(
    {
      onSuccess: (response) => {
        const updatedData = response?.data?.data;

        if (updatedData && Object.values(updatedData).length > 0) {
          const updatedStep = Object.values(updatedData).find(
            (step) => step.Step_Number === stepDetails.Step_Number,
          );

          if (updatedStep) {
            dispatch(setEditStep({ ...stepDetails, ...updatedStep }));
          }
        }

        queryClient.invalidateQueries({ queryKey: ["testCaseResults"] });
        queryClient.invalidateQueries({ queryKey: ["latestRecordings"] });
        notify("Reference variable created successfully.", "success");
      },
      onError: (error) => {
        notify(error?.response?.data?.message || "Failed to create reference variable", "error");
      },
    },
  );

  useEffect(() => {
    updateButtonDisableStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEditItemIndex, testCasesList]);

  const updateButtonDisableStatus = () => {
    if (currentEditItemIndex !== -1) {
      setDisableIncrement(currentEditItemIndex === testCasesList.length - 1);
      setDisableDecrement(currentEditItemIndex === 0);
    } else {
      setDisableIncrement(true);
      setDisableDecrement(true);
    }
  };

  const handleIncrementStep = () => {
    if (currentEditItemIndex !== -1) {
      if (currentEditItemIndex + 1 < testCasesList.length) {
        dispatch(setEditItemIndex(currentEditItemIndex + 1));
      } else {
        console.log("End of test cases list reached");
      }
    }
  };

  const handleDecrementStep = () => {
    if (currentEditItemIndex !== -1) {
      if (currentEditItemIndex - 1 >= 0) {
        dispatch(setEditItemIndex(currentEditItemIndex - 1));
      } else {
        console.log("Beginning of test cases list reached");
      }
    }
  };

  const handleBack = () => {
    dispatch(setEditItemIndex(null));
  };

  const handleAddStepClick = () => {
    const payload = {
      Keyword: stepDetails.Keyword,
      Action: stepDetails.Action,
      Flow: stepDetails.Flow,
      Page_Description: stepDetails.Page_Description,
      Page_Name: stepDetails.Page_Name,
      TestData: stepDetails?.TestData,
      Test_Case_Id: stepDetails.Test_Case_Id,
      Test_Step_Id: stepDetails.idtest_step,
      stepnumber: stepDetails.Step_Number,
      wait: stepDetails.wait,
      waittime: stepDetails.waittime,
      subAction: stepDetails.subAction,
      teststepthreshold: stepDetails.teststepthreshold,
      iframexpath: stepDetails.iframexpath,
      tabid: stepDetails.tabid,
      VarName: stepDetails.VarName,
      varType: stepDetails.varType,
      type: stepDetails.type,
      isDynamic: stepDetails.isDynamic,
      createsAlert: stepDetails.createsAlert,
      useLocation: stepDetails.useLocation,
      isXYGraph: stepDetails.isXYGraph,
      ObjectIdentifier: stepDetails.Object_Xpath,
      inlinedepedence: stepDetails.inlinedepedence,
    };
    handleAddStep(payload);
    handleBack();
  };
  const handleblankstepclick = () => {
    const payload = {
      Keyword: "",
      Action: "",
      Flow: "",
      Page_Description: "",
      Page_Name: "",
      TestData: "",
      Test_Case_Id: stepDetails.Test_Case_Id,
      stepnumber: stepDetails.Step_Number,
      tsid: stepDetails.idtest_step,
      wait: "",
      waittime: "",
      subAction: "",
      teststepthreshold: "",
      iframexpath: "",
      tabid: stepDetails.tabid,
      VarName: "",
      type: "",
      isDynamic: "",
      createsAlert: "",
      useLocation: "",
      ObjectIdentifier: "",
      inlinedepedence: "",
    };
    addBlankStep(payload);
    handleBack();
  };
  const handleSaveStepClick = () => {
    const payload = {
      Keyword: stepDetails.Keyword,
      Action: stepDetails.Action,
      Flow: stepDetails.Flow,
      Page_Description: stepDetails.Page_Description,
      Page_Name: stepDetails.Page_Name,
      TestData: stepDetails?.TestData,
      Test_Case_Id: stepDetails.Test_Case_Id,
      tsid: stepDetails.idtest_step,
      wait: stepDetails.wait,
      waittime: stepDetails.waittime,
      subAction: stepDetails.subAction,
      teststepthreshold: stepDetails.teststepthreshold,
      iframexpath: stepDetails.iframexpath,
      tabid: stepDetails.tabid,
      VarName: stepDetails.VarName,
      varType: stepDetails.varType,
      type: stepDetails.type,
      isDynamic: stepDetails.isDynamic,
      createsAlert: stepDetails.createsAlert,
      useLocation: stepDetails.useLocation,
      isXYGraph: stepDetails.isXYGraph,
      ObjectIdentifier: stepDetails.Object_Xpath,
      inlinedepedence: stepDetails.inlinedepedence,
      condLogic: stepDetails.condLogic,
      condexp: stepDetails.condexp,
      stepExpression: stepDetails.stepExpression,
      initialization: stepDetails.initialization,
      terminateCondition: stepDetails.terminateCondition,
      ifCondition: stepDetails.ifCondition,
      assertionExpression: stepDetails.assertionExpression,
      testProcessingtype: stepDetails.testProcessingtype,
      testProcessingobj: stepDetails.testProcessingobj,
      updateRecordData: stepDetails.updateRecordData ?? false,
      backgroundColor: stepDetails.bgcolor,
      color: stepDetails.color,
    };
    handleSaveStep(payload);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    setAnchorEl(null);
  };

  const handleExitEdit = () => {
    handleBack();
  };

  const handleCreateReferenceVariable = () => {
    if (!stepDetails || stepDetails.Step_Number === 0) return;

    const updatedStep = { ...stepDetails, testdata_source: "IsAVar" };
    dispatch(setEditStep(updatedStep));

    updateTestData({
      api_input: updatedStep,
      fromStrategy: false,
      modid: selectedModuleID,
    });
  };

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" mt={1.5}>
      <Box>
        <Typography color="#000000" fontSize="25px" fontWeight={600}>
          <Tooltip title="Go to previous step">
            <StyledIconBtn
              variant="text"
              aria-label="Previous step"
              disabled={disableDecrement}
              onClick={handleDecrementStep}
            >
              <LeftArrowIcon />
            </StyledIconBtn>
          </Tooltip>
          <StepText>Step {editItem?.Step_Number}</StepText>
          <Tooltip title="Go to next step">
            <StyledIconBtn
              aria-label="Next step"
              disabled={disableIncrement}
              onClick={handleIncrementStep}
            >
              <RightArrowIcon />
            </StyledIconBtn>
          </Tooltip>
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" columnGap="12px" textAlign="right">
        <Tooltip title="Exit edit mode and return to test steps list">
          <Button
            variant="outlined"
            sx={{
              ...sharedActionButtonSx,
            }}
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Exit Edit
          </Button>
        </Tooltip>
        <Tooltip title="Add a new blank step after the current step">
          <Button
            color="primary"
            sx={{
              ...sharedActionButtonSx,
              backgroundColor: "#2550d1",
              boxShadow: "0px 8px 18px rgba(37, 80, 209, 0.22)",
              minWidth: "120px",
            }}
            variant="contained"
            startIcon={<PlusIcon />}
            disabled={isReadOnly}
            onClick={handleblankstepclick}
          >
            Add Step
          </Button>
        </Tooltip>
        <Tooltip title="Clone current step with all its configuration">
          <Button
            color="primary"
            sx={{
              ...sharedActionButtonSx,
              backgroundColor: "#2550d1",
              boxShadow: "0px 8px 18px rgba(37, 80, 209, 0.22)",
              minWidth: "130px",
            }}
            variant="contained"
            startIcon={<PlusIcon />}
            disabled={isReadOnly}
            onClick={handleAddStepClick}
          >
            Clone Step
          </Button>
        </Tooltip>
        <Tooltip title="Save all changes made to this step">
          <Button
            color="warning"
            sx={{
              ...sharedActionButtonSx,
              backgroundColor: "#ff5336",
              boxShadow: "0px 8px 18px rgba(255, 83, 54, 0.22)",
              minWidth: "140px",
            }}
            variant="contained"
            startIcon={<BookmarkIcon />}
            disabled={isReadOnly}
            onClick={handleSaveStepClick}
          >
            Save Changes
          </Button>
        </Tooltip>
        <Tooltip title="Create current step as a reference variable">
          <Button
            variant="contained"
            sx={{
              ...sharedActionButtonSx,
              backgroundColor: "#2550d1",
              color: "#ffffff",
              boxShadow: "0px 8px 18px rgba(37, 80, 209, 0.22)",
              "&:hover": {
                backgroundColor: "#1a3db2",
              },
            }}
            onClick={handleCreateReferenceVariable}
            disabled={isReadOnly || stepDetails?.Step_Number === 0 || isCreatingReferenceVariable}
            startIcon={<Package size={16} />}
          >
            {isCreatingReferenceVariable ? "Creating..." : "Create Variable"}
          </Button>
        </Tooltip>
        <Tooltip title="More options">
          <Box onClick={handleMenuClick}>
            <IconContainerCircle>
              <ContextIcon />
            </IconContainerCircle>
          </Box>
        </Tooltip>
        <CustomMenu
          anchorEl={anchorEl}
          handleClose={handleMenuClose}
          handleExitEdit={handleExitEdit}
          handleMerge={handleMerge}
        />
      </Box>
    </Box>
  );
};
export default EditHeader;
